import React, { useEffect, useMemo, useState } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Typography, Paper, Grid, Button, TextField, InputAdornment, Chip, IconButton } from '@material-ui/core';
import { ExpandMore, Search, Close, ThumbUpAltOutlined, ThumbUpAlt, ThumbDownAltOutlined, ThumbDownAlt, UnfoldMore, UnfoldLess, HelpOutline, HomeOutlined, GroupOutlined, TuneOutlined, SettingsOutlined, DescriptionOutlined, BuildOutlined, ForumOutlined, FolderOutlined, AssignmentOutlined, FlightOutlined, WhatshotRounded, RemoveRedEyeOutlined } from '@material-ui/icons';
import { checkApiStatus } from '../../utils_v2';
import { globalGetService, globalPostService } from '../../../globalServices';

const CATEGORY_ICONS = {
    'Console': TuneOutlined,
    'Home Page': HomeOutlined,
    'Assets': FolderOutlined,
    'User Management': GroupOutlined,
    'Settings': SettingsOutlined,
    'Lease Management': DescriptionOutlined,
    'Maintenance Management': BuildOutlined,
    'CommVerge': ForumOutlined,
    'Records Dataroom': FolderOutlined,
    'Projects Management': AssignmentOutlined,
    'Airworthiness Management': FlightOutlined
};

const COLORS = {
    primary: '#6252c7',
    primarySoft: '#EEEAFF',
    accent: '#16866f',
    accentSoft: '#E6F7F3',
    warn: '#d37a0d',
    warnSoft: '#FFF3DF',
    danger: '#DF5360',
    dangerSoft: '#FEECEE',
    textMuted: '#6d7187',
    border: '#E7E8F0',
    pageBg: '#F6F7FB'
};

const ACCENT_ROTATION = ['#6252c7', '#dd6b20', '#087ea4', '#c44f76', '#1676a5', '#db8c12', '#16866f', '#70843b'];

const CATEGORY_QUERY_MAP = {
    'Console': 'console',
    'Lease Management': 'lease-management',
    'Maintenance Management': 'maintenance-management',
    'CommVerge': 'commverge',
    'Records Dataroom': 'records-dataroom',
    'Projects Management': 'projects-management',
    'Airworthiness Management': 'airworthiness-management',
    'fin-S':'fin-s'
};
const MODULE_WISE_QUERY = {
    1: "Console",
    2: "Maintenance Claim Management",
    3: "Lease Management",
    4: "CommVerge",
    6: "Records Dataroom",
    8: "Projects Management",
    14: "fin-S",
    18: "Airworthiness Management", 
    33: "Lease Management"
}

const getCategoryIcon = (section) => CATEGORY_ICONS[section] || HelpOutline;
const getCategoryAccent = (section, categories) => ACCENT_ROTATION[categories.indexOf(section) % ACCENT_ROTATION.length];

const isHtml = (str) => /<\/?[a-z][\s\S]*>/i.test(str || '');
const stripHtml = (str) => (str || '').replace(/<[^>]*>/g, ' ');
const faqKey = (section, faq) => `${section}::${faq?.id}`;

const highlightMatch = (text, term) => {
    if (!term) return text;
    const idx = text.toLowerCase().indexOf(term.toLowerCase());
    if (idx === -1) return text;
    return (
        <>
            {text.slice(0, idx)}
            <mark style={{ background: COLORS.primarySoft, color: COLORS.primary, padding: 0 }}>
                {text.slice(idx, idx + term.length)}
            </mark>
            {text.slice(idx + term.length)}
        </>
    );
};

const getCategoryFromUrl = () => {
    const query = new URLSearchParams(window.location?.search || '').get('application');
    if (!query) return 'All';
    if (MODULE_WISE_QUERY[query]) return MODULE_WISE_QUERY[query];
    const match = Object.entries(CATEGORY_QUERY_MAP).find(([, slug]) => slug === query);
    return match ? match[0] : 'All';
};

export default function FAQs() {
    const [faqnewData, setFaqnewData] = useState({});
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(getCategoryFromUrl);
    const [expandedSections, setExpandedSections] = useState([]);
    const [expandedFaqs, setExpandedFaqs] = useState([]);
    const [feedback, setFeedback] = useState({});

    useEffect(() => {
        setLoading(true);
        globalGetService('console/faqs/')
            .then((response) => {
                if (checkApiStatus(response)) {
                    const responseCategories = response?.data?.data?.categories || {};
                    const initialFeedback = {};
                    Object.values(responseCategories).forEach((faqs) => {
                        (faqs || []).forEach((faq) => {
                            if (faq?.id != null) initialFeedback[faq.id] = faq?.user_vote || null;
                        });
                    });
                    setFaqnewData(responseCategories);
                    setStats(response?.data?.data?.stats || {});
                    setFeedback(initialFeedback);
                }
                setLoading(false);
            });
    }, []);

    const categories = useMemo(() => Object.keys(faqnewData), [faqnewData]);
    const activeCategory = selectedCategory !== 'All' && !categories.includes(selectedCategory) ? 'All' : selectedCategory;

    const filteredData = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return Object.entries(faqnewData).reduce((acc, [section, faqs]) => {
            if (activeCategory !== 'All' && section !== activeCategory) return acc;
            const sorted = [...(faqs || [])].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
            const matched = term
                ? sorted.filter((faq) => (
                    (faq?.question || '').toLowerCase().includes(term) ||
                    stripHtml(faq?.answer).toLowerCase().includes(term)
                ))
                : sorted;
            if (matched.length) acc[section] = matched;
            return acc;
        }, {});
    }, [faqnewData, searchTerm, activeCategory]);

    const activeCounts = useMemo(() => (
        Object.fromEntries(categories.map((section) => [
            section,
            (faqnewData[section] || []).length
        ]))
    ), [faqnewData, categories]);

    const totalFaqsAll = useMemo(
        () => Object.values(activeCounts).reduce((sum, count) => sum + count, 0),
        [activeCounts]
    );

    const totalCategoriesAll = categories.length;

    const helpfulVotes = useMemo(
        () => Object.values(feedback).filter((v) => v === 'up').length,
        [feedback]
    );

    const filteredTotal = useMemo(
        () => Object.values(filteredData).reduce((sum, faqs) => sum + faqs.length, 0),
        [filteredData]
    );

    const filteredCategoriesCount = Object.keys(filteredData).length;

    const lastUpdated = useMemo(
        () => new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
        []
    );

    const allKeys = useMemo(() => {
        const sectionKeys = Object.keys(filteredData);
        const faqKeys = Object.entries(filteredData).flatMap(([section, faqs]) => (
            faqs.map((faq) => faqKey(section, faq))
        ));
        return { sectionKeys, faqKeys };
    }, [filteredData]);

    const allExpanded = allKeys.sectionKeys.length > 0 &&
        allKeys.sectionKeys.every((s) => expandedSections.includes(s)) &&
        allKeys.faqKeys.every((k) => expandedFaqs.includes(k));

    const toggleSection = (section, isExpanded) => {
        setExpandedSections((prev) => (
            isExpanded ? [...prev, section] : prev.filter((s) => s !== section)
        ));
    };

    const toggleFaq = (key, faqId, isExpanded) => {
        setExpandedFaqs((prev) => (
            isExpanded ? [...prev, key] : prev.filter((k) => k !== key)
        ));
        if (isExpanded) {
            globalPostService(`console/faqs/${faqId}/view/`);
        }
    };

    const handleToggleExpandAll = () => {
        if (allExpanded) {
            setExpandedSections([]);
            setExpandedFaqs([]);
        } else {
            setExpandedSections(allKeys.sectionKeys);
            setExpandedFaqs(allKeys.faqKeys);
        }
    };

    const handleTopicClick = (name) => {
        setSelectedCategory('All');
        setSearchTerm(name);
    };

    const handleFeedback = (faqId, type) => {
        const nextValue = feedback[faqId] === type ? null : type;
        setFeedback((prev) => ({ ...prev, [faqId]: nextValue }));
        globalPostService(`console/faqs/${faqId}/feedback/`, { value: nextValue });
    };

    return (
        <section className='faq-page-container'>
            <div className='faq-body'>
                <div className='faq-hero'>
                    <div>
                        <Typography variant='h5' className='faq-hero__title'>Frequently Asked Questions</Typography>
                        <Typography variant='body2' className='faq-hero__subtitle'>Find quick answers and guidance across SPARTA modules.</Typography>
                    </div>
                    <div className='faq-hero__meta'>
                        <span className='faq-hero__pill'> Updated {lastUpdated} </span>
                    </div>
                </div>
                <Grid container spacing={2} alignItems='stretch'>
                    <Grid item md={9} xs={12}>
                        <Paper className='faq-filter-card'>
                            <div className='faq-filter-row'>
                                <div className='faq-search-wrap'>
                                    <TextField
                                        placeholder='Search questions, answers or keywords...'
                                        fullWidth
                                        size='small'
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        variant='outlined'
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position='start'>
                                                    <Search fontSize='small' />
                                                </InputAdornment>
                                            ),
                                            endAdornment: searchTerm ? (
                                                <InputAdornment position='end'>
                                                    <IconButton size='small' onClick={() => setSearchTerm('')}>
                                                        <Close fontSize='small' />
                                                    </IconButton>
                                                </InputAdornment>
                                            ) : undefined
                                        }}
                                    />
                                </div>
                                <Button
                                    variant='outlined'
                                    color='inherit'
                                    startIcon={allExpanded ? <UnfoldLess fontSize='small' /> : <UnfoldMore fontSize='small' />}
                                    className='faq-expand-btn'
                                    onClick={handleToggleExpandAll}
                                    disabled={allKeys.sectionKeys.length === 0}
                                >
                                    {allExpanded ? 'Collapse All' : 'Expand All'}
                                </Button>
                            </div>

                            <div className='faq-meta-row'>
                                <span className='faq-meta-pill'>
                                    {filteredTotal} matches in {filteredCategoriesCount} categories
                                </span>
                                <span className='faq-meta-text'> Showing categories you have access to </span>
                            </div>

                            <div className='faq-category-row'>
                                <Chip
                                    disableRipple
                                    onClick={() => setSelectedCategory('All')}
                                    className={activeCategory === 'All' ? 'faq-chip is-active' : 'faq-chip'}
                                    label={
                                        <span className='faq-chip__label'>
                                            <span>All</span>
                                            <span className={activeCategory === 'All' ? 'faq-chip__count is-active' : 'faq-chip__count'}>{totalFaqsAll}</span>
                                        </span>
                                    }
                                />
                                {categories.map((section) => {
                                    const SectionIcon = getCategoryIcon(section);
                                    const active = activeCategory === section;
                                    return (
                                        <Chip
                                            key={section}
                                            disableRipple
                                            onClick={() => setSelectedCategory(section)}
                                            className={active ? 'faq-chip is-active' : 'faq-chip'}
                                            label={
                                                <span className='faq-chip__label'>
                                                    <span>{section}</span>
                                                    <span className={active ? 'faq-chip__count is-active' : 'faq-chip__count'}>{activeCounts[section] || 0}</span>
                                                </span>
                                            }
                                        />
                                    );
                                })}
                            </div>
                        </Paper>

                        <Paper className='faq-section-panel'>
                            <div className='faq-section-panel__header'>
                                <Typography variant='body1' className='faq-section-panel__title'>
                                    {filteredTotal} FAQs across {filteredCategoriesCount} categories
                                </Typography>
                                <Typography variant='body1' className='faq-section-panel__updated'>
                                    Updated {lastUpdated}
                                </Typography>
                            </div>

                            <div className='faq-section-panel__body'>
                                {loading && (
                                    <div className='faq-empty-state'>
                                        <Typography variant='body2'>Loading FAQs...</Typography>
                                    </div>
                                )}
                                {!loading && Object.keys(filteredData).length === 0 && (
                                    <div className='faq-empty-state'>
                                        <div className='faq-empty-state__card'>
                                            <div className='faq-empty-state__icon'>
                                                <Search style={{ fontSize: 28 }} />
                                            </div>
                                            <Typography variant='h6' className='faq-section-title'> No matching FAQs </Typography>
                                            <Typography variant='body2' className='faq-empty-state__text'>
                                                Try a different keyword or clear the current filters to see more questions.
                                            </Typography>
                                            {(searchTerm || activeCategory !== 'All') && (
                                                <div className='faq-empty-state__actions'>
                                                    <Button
                                                        size='small'
                                                        variant='contained'
                                                        className='faq-empty-state__button'
                                                        onClick={() => {
                                                            setSearchTerm('');
                                                            setSelectedCategory('All');
                                                        }}
                                                    >
                                                        Clear filters
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {Object.entries(filteredData).map(([section, faqs]) => {
                                    const SectionIcon = getCategoryIcon(section);
                                    const accent = getCategoryAccent(section, categories);

                                    return (
                                        <Accordion
                                            key={section}
                                            elevation={0}
                                            square
                                            disableGutters
                                            expanded={expandedSections.includes(section)}
                                            onChange={(e, isExp) => toggleSection(section, isExp)}
                                            className='faq-accordion'
                                            style={{ '--faq-accent': accent }}
                                        >
                                            <AccordionSummary expandIcon={<ExpandMore />} className='faq-accordion__summary'>
                                                <div className='faq-accordion__summary-inner'>
                                                    <SectionIcon fontSize='small' className='faq-accent-icon' />
                                                    <Typography variant='body1' className='faq-section-text'>
                                                        {section}
                                                    </Typography>
                                                    <Chip
                                                        label={`${faqs?.length || 0} FAQs`}
                                                        size='small'
                                                        className='faq-faq-chip'
                                                    />
                                                </div>
                                            </AccordionSummary>

                                            <AccordionDetails className='faq-sub-accordion__details'>
                                                {(faqs || []).map((faq) => {
                                                    const key = faqKey(section, faq);
                                                    const isOpen = expandedFaqs.includes(key);

                                                    return (
                                                        <Accordion
                                                            key={faq?.id}
                                                            elevation={0}
                                                            square
                                                            disableGutters
                                                            expanded={isOpen}
                                                            onChange={(e, nextExpanded) => toggleFaq(key, faq?.id, nextExpanded)}
                                                            className={`faq-sub-accordion${isOpen ? ' is-open' : ''}`}
                                                        >
                                                            <AccordionSummary expandIcon={<ExpandMore />} className='faq-sub-accordion__summary'>
                                                                <div className='faq-sub-accordion__summary-inner'>
                                                                    <Typography variant='body2' className='faq-question-text'>
                                                                        {highlightMatch(faq?.question || '', searchTerm)}
                                                                    </Typography>
                                                                </div>
                                                            </AccordionSummary>
                                                            <AccordionDetails className='faq-sub-accordion__body'>
                                                                {isHtml(faq?.answer)
                                                                    ? <div style={{ fontSize: "14px", lineHeight: 1.6, color: "#374151", }} dangerouslySetInnerHTML={{ __html: faq?.answer }} />
                                                                    : <Typography variant='body2'>{faq?.answer}</Typography>}

                                                                <div className='faq-help-row'>
                                                                    <Typography variant='body2' className='faq-help-text'>Was this helpful?</Typography>
                                                                    <IconButton size='small' onClick={() => handleFeedback(faq?.id, 'up')}>
                                                                        {feedback[faq?.id] === 'up'
                                                                            ? <ThumbUpAlt fontSize='small' style={{ color: COLORS.primary }} />
                                                                            : <ThumbUpAltOutlined fontSize='small' />}
                                                                    </IconButton>
                                                                    <IconButton size='small' onClick={() => handleFeedback(faq?.id, 'down')}>
                                                                        {feedback[faq?.id] === 'down'
                                                                            ? <ThumbDownAlt fontSize='small' style={{ color: COLORS.danger }} />
                                                                            : <ThumbDownAltOutlined fontSize='small' />}
                                                                    </IconButton>
                                                                    {feedback[faq?.id] && (
                                                                        <Typography variant='caption' style={{ color: COLORS.textMuted }}>
                                                                            Thanks for your feedback!
                                                                        </Typography>
                                                                    )}
                                                                </div>
                                                            </AccordionDetails>
                                                        </Accordion>
                                                    );
                                                })}
                                            </AccordionDetails>
                                        </Accordion>
                                    );
                                })}
                            </div>
                        </Paper>
                    </Grid>

                    <Grid item md={3} xs={12}>
                        <div className='faq-sidebar'>
                            <Paper className='faq-sidebar-card'>
                                <Typography variant='body1' className='faq-section-title'>
                                    FAQ Overview
                                </Typography>

                                <div className='faq-overview-stack'>
                                    <div className='faq-overview-row' style={{ background: COLORS.primarySoft }}>
                                        <Typography variant='body2' style={{ color: COLORS.textMuted }}>Categories</Typography>
                                        <Typography variant='body1' className='faq-overview-value' style={{ color: COLORS.primary }}>{totalCategoriesAll}</Typography>
                                    </div>
                                    <div className='faq-overview-row' style={{ background: COLORS.accentSoft }}>
                                        <Typography variant='body2' style={{ color: COLORS.textMuted }}>Most viewed category</Typography>
                                        <Typography variant='body2' className='faq-overview-value' style={{ color: COLORS.accent }}>{stats?.most_viewed_category || '—'}</Typography>
                                    </div>
                                    <div className='faq-overview-row' style={{ background: COLORS.warnSoft, gap: 8 }}>
                                        <Typography variant='body2' style={{ color: COLORS.textMuted, whiteSpace: 'nowrap' }}>Most liked category</Typography>
                                        <Typography variant='body2' className='faq-overview-value' style={{ color: COLORS.warn, textAlign: 'right' }} noWrap>
                                            {stats?.most_liked_category || '—'}
                                        </Typography>
                                    </div>
                                    <div className='faq-overview-row' style={{ background: COLORS.dangerSoft }}>
                                        <Typography variant='body2' style={{ color: COLORS.textMuted }}>Marked helpful</Typography>
                                        <Typography variant='body1' className='faq-overview-value' style={{ color: COLORS.danger }}>{helpfulVotes}</Typography>
                                    </div>
                                </div>
                            </Paper>

                            <Paper elevation={0} className='faq-popular-card'>
                                <div className='faq-popular-card__header'>
                                    <div className='faq-popular-card__heading'>
                                        <div className='faq-popular-card__icon'> <WhatshotRounded /> </div>
                                        <div>
                                            <Typography className='faq-popular-card__title'>Popular topics</Typography>
                                            <Typography className='faq-popular-card__subtitle'>Most viewed articles</Typography>
                                        </div>
                                    </div>
                                </div>
                                <div className='faq-popular-card__divider' />
                                <div>
                                    {stats?.most_viewed_questions?.map((item, index) => (
                                        <div
                                            key={item?.id}
                                            onClick={() => handleTopicClick(item?.question)}
                                            className='faq-popular-row'
                                        >
                                            <div className='faq-popular-row__number'> {(index + 1).toString().padStart(2, '0')} </div>
                                            <Typography className='faq-popular-row__title'>{item?.question}</Typography>
                                            <div className='faq-popular-row__views'>
                                                <RemoveRedEyeOutlined fontSize='small' />
                                                <Typography className='faq-popular-row__views-text'>{item?.views} views</Typography>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Paper>

                            <Paper className='faq-sidebar-card faq-sidebar-card--support'>
                                <div className='faq-sidebar__support-header'>
                                    <HelpOutline fontSize='small' className='faq-sidebar__support-icon' />
                                    <Typography variant='body1' className='faq-section-title'>Still need help?</Typography>
                                </div>
                                <div className='faq-sidebar__support-body'>
                                    <Typography variant='body2' className='faq-sidebar__support-copy'>Our support team is here to help with any SPARTA question.</Typography>
                                    <div style={{ height: 10 }} />
                                    <Button variant='contained' className='faq-sidebar__support-button' fullWidth component='a' href='mailto:support@sparta.aero?subject=SPARTA%20Support%20Request' >Contact Support</Button>
                                    <div style={{ height: 10 }} />
                                    <Typography variant='body2' align='center' className='faq-sidebar__support-email'>support@sparta.aero</Typography>
                                </div>
                            </Paper>
                        </div>
                    </Grid>
                </Grid>
            </div>
        </section>
    );
}
